/*
 * Styles for <agwa-lit-table>.
 *
 * Imported by data-table.ts via `?inline` + `unsafeCSS` so this stays a real
 * .css file (CSS language server, CSS-aware prettier, and stylelint all work on
 * it) while still being adopted into the component's shadow root as a single
 * constructable stylesheet.
 *
 * COLOUR RULE: no raw hex in here. Every colour comes from a semantic role in
 * src/styles/tokens.css, which is where light/dark is decided exactly once.
 * The --data-table-* variables below remain the component's public override
 * API - a host can still retint one table - but their DEFAULTS are roles, not
 * literals, which is what makes every list page theme correctly for free.
 */

:host {
  /* --- Colour surface, all derived from the app's semantic roles ---------- */
  --data-table-bg: var(--agwa-surface-raised);
  --data-table-border: var(--agwa-border-strong);
  --data-table-divider: var(--agwa-divider);
  --data-table-header-bg: var(--agwa-accent-strong);
  /* Header text sits ON the brand accent fill, so it must NOT follow the
     neutral text roles - those invert, and inverting here would put near-black
     text on blue in dark mode. See --agwa-on-accent in tokens.css. */
  --data-table-header-color: var(--agwa-on-accent);
  --data-table-row-hover: var(--agwa-surface-hover);
  --data-table-row-selected: var(--agwa-surface-selected);
  /* Child rows and row extensions both sit INSIDE a row, so both take the nested surface rather than
     the sunken one. Sunken's offset from a raised row is about 2%, which the child-row rule below
     was already relying on to say "subordinate" and which was too faint to read as a cue at all. */
  --data-table-row-extension-bg: var(--agwa-surface-nested);
  --data-table-child-row-hover: var(--agwa-surface-nested-hover);
  --data-table-radius: 0;
  --data-table-font:
    'Space Grotesk', 'Segoe UI', system-ui, -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', sans-serif;

  /*
   * Density tokens - tuned for a compact, legible, spreadsheet-like feel.
   * Override any of these on a host to adjust density without touching the
   * component. Defaults sit around 90% of the previous sizing.
   */
  --data-table-cell-font-size: 0.75rem;
  --data-table-header-font-size: 0.7rem;
  --data-table-cell-line-height: 1.2;
  --data-table-cell-padding-y: 0.2rem;
  --data-table-cell-padding-x: 0.6rem;
  --data-table-row-min-height: 1.5rem;
  /* Height of embedded controls (action buttons) inside a cell. Status
     tags/badges size off the cell font. Keeping these small is what stops
     rows growing taller than the text. */
  --data-table-control-height: 1.4rem;

  display: block;
  width: 100%;
}

/* When placed in a flex container, fill available height */
:host([fill-height]) {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

:host([fill-height]) .data-table.data-table {
  flex: 1 1 auto;
  min-height: 0;
}

:host([fill-height]) .data-table-scroll {
  max-height: none !important; /* Override standardtable/halftable max-heights */
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

.data-table.data-table {
  display: flex;
  flex-direction: column;
  background: var(--data-table-bg);
  border: 1px solid var(--data-table-border);
  border-radius: var(--data-table-radius);
  /* Drop shadow plus a hairline inner highlight. Both roles change in dark:
     the shadow densifies (a light-mode shadow is invisible on a dark base) and
     the highlight nearly vanishes (a bright inner edge would ring the table). */
  box-shadow:
    var(--agwa-shadow-lg),
    inset 0 0 0 1px var(--agwa-inset-highlight);
  overflow: hidden;
  overflow-anchor: none;
  font-family: var(--data-table-font);
}

.data-table-header {
  background: var(--data-table-header-bg);
  color: var(--data-table-header-color);
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  width: 100%;
  min-width: max-content;
}

.data-table-header-row {
  display: flex;
  align-items: stretch;
  min-height: var(--data-table-row-min-height);
  width: 100%;
  min-width: max-content;
  font-size: var(--data-table-header-font-size);
  letter-spacing: 0.02em;
  text-transform: uppercase;
  font-weight: 600;
  will-change: transform;
}

.data-table-header-segment {
  display: flex;
  position: relative;
  z-index: 1;
  background: var(--data-table-header-bg);
}

/* Sticky edge columns cast a shadow over the scrolling centre track so the
   frozen boundary stays readable while the user scrolls sideways. The role is
   colour-only because the offset direction differs per side. */
.data-table-header-segment-left {
  flex: 0 0 auto;
  position: sticky;
  left: 0;
  z-index: 3;
  box-shadow: 8px 0 12px var(--agwa-shadow-edge);
}

.data-table-header-segment-right {
  flex: 0 0 auto;
  width: max-content;
  z-index: 3;
}

/*
  A right-pinned column pins only while the table is wider than its window (the component's
  overflowsSideways, settings-lookup-tables-acceptance.md rule 1). Pinned, it sits at the right
  edge over the scrolling centre with its shadow. Otherwise the centre stops growing and the column
  follows the last one as an ordinary column: pushed to the far edge of a table that fits, it left a
  wide empty band between the data and its actions. Left-pinned columns leave no band, so they pin
  always.
*/
.data-table--overflows-x .data-table-header-segment-right {
  position: sticky;
  right: 0;
  box-shadow: -8px 0 12px var(--agwa-shadow-edge);
}

.data-table:not(.data-table--overflows-x)
  .data-table-header-segment-center:has(+ .data-table-header-segment-right),
.data-table:not(.data-table--overflows-x)
  .data-table-row-segment-center:has(+ .data-table-row-segment-right) {
  flex: 0 0 auto;
}

/*
  With the centre no longer growing, the header row's own fill shows to the right of the last
  heading; it is also a .data-table-row, whose fill is the body's, so it is given the header's.
*/
.data-table:not(.data-table--overflows-x) .data-table-header-row {
  background: var(--data-table-header-bg);
}

.data-table-header-segment-center {
  flex: 1 1 auto;
  z-index: 0;
  position: relative;
}

.data-table-header-center-track {
  display: flex;
}

/* Row segment layout.
   Child rows share this rule rather than copying it: one set of layout declarations means a child
   row cannot fall out of geometric step with its parent, which is the whole reason child rows exist
   instead of a consumer-owned nested table. */
.data-table-row,
.data-table-child-row {
  display: flex;
  align-items: stretch;
  min-height: var(--data-table-row-min-height);
  width: 100%;
  min-width: max-content;
  position: relative;
  border-bottom: 1px solid var(--data-table-divider);
  background: var(--data-table-bg);
  transition: background 160ms ease;
}

/* Clickable rows (clickrows / selectable) get role="button" - show the
   pointer cursor so the row reads as actionable. */
.data-table-child-row[role='button'],
.data-table-row[role='button'] {
  cursor: pointer;
}

/*
  A child row belongs to the row above it, and everything here says "subordinate" rather than
  "different". It keeps the parent's grid exactly - same segments, same widths - so the only signals
  are a sunken fill and a rail down the left edge.

  Deliberately NOT a heading, a band or a fill of its own colour. The nested sites table this
  replaced announced itself with a solid accent-coloured header row, which made the detail the
  loudest thing on screen and read as a divider between companies rather than as part of one. The
  rule the #334 audit trail settled applies here too: detail must never outweigh the thing it sits
  in.
*/
.data-table-child-row {
  background: var(--data-table-row-extension-bg);
}

/*
  The rail is drawn as an inset shadow rather than a border so it costs no horizontal space: a border
  would shift every child cell by its width and break the column alignment that is the entire reason
  child rows exist.
*/
.data-table-child-row::before {
  content: '';
  position: absolute;
  inset-block: 0;
  inset-inline-start: 0;
  width: 3px;
  background: var(--agwa-accent);
}

/* The last child of a group closes against its parent's block rather than floating free. */
.data-table-child-row:has(+ .data-table-row) {
  border-bottom-width: 2px;
}

.data-table-row-segment {
  display: flex;
  position: relative;
}

.data-table-row-segment-left {
  flex: 0 0 auto;
  z-index: 2;
  background: var(--data-table-bg);
  position: sticky;
  left: 0;
  box-shadow: 8px 0 12px var(--agwa-shadow-edge);
}

.data-table-row-segment-right {
  flex: 0 0 auto;
  z-index: 2;
  background: var(--data-table-bg);
}

/* Pinned only while the table overflows sideways; see the header's right segment above. */
.data-table--overflows-x .data-table-row-segment-right {
  position: sticky;
  right: 0;
  box-shadow: -8px 0 12px var(--agwa-shadow-edge);
  margin-left: auto;
}

/*
  The pinned segments paint their own background so the scrolling centre track cannot show through
  them. That means a child row's fill has to be restated here, or the row would read as sunken in the
  middle and normal at both frozen edges - the alignment would be right and the surface would lie.
*/
.data-table-child-row .data-table-row-segment-left,
.data-table-child-row .data-table-row-segment-right {
  background: var(--data-table-row-extension-bg);
}

/* Ensure hover/selected states on sticky segments */
.data-table-row:hover .data-table-row-segment-left,
.data-table-row:hover .data-table-row-segment-right {
  background: var(--data-table-row-hover);
}

.data-table-row.selected .data-table-row-segment-left,
.data-table-row.selected .data-table-row-segment-right {
  background: var(--data-table-row-selected);
}

.data-table-row-segment-center {
  flex: 1 1 auto;
  z-index: 0;
  position: relative;
}

.data-table-row-center-track {
  display: flex;
}

.data-table-scroll {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: auto;
  background: var(--data-table-bg);
}

/* Once the header sticks, it needs its own elevation against scrolled rows.
   The bottom rule is an on-accent divider, not a neutral border - it sits on
   the blue header fill and so must stay white-translucent in both themes. */
.data-table-header--sticky {
  box-shadow: var(--agwa-shadow-sm);
  border-bottom: 1px solid var(--agwa-on-accent-divider);
}

.data-table-content {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: max-content;
}

.data-table.standardtable .data-table-scroll {
  max-height: 70vh;
}

.data-table.nestedtable .data-table-scroll {
  max-height: none;
}

.data-table.halftable .data-table-scroll {
  max-height: 35vh;
}

.data-table-row:hover {
  background: var(--data-table-row-hover);
}

/*
  A child row is clickable, so it must answer the pointer. It hovers to its OWN token rather than the
  parent's: a child rests on the nested surface, so reusing --data-table-row-hover would measure the
  change from the wrong starting colour and, in dark, land a resting child and a hovered parent on
  nearly the same value.

  The sticky segments are restated for the same reason the resting fill is - they are separate
  stacking contexts, so a rule on the row alone leaves the frozen edges unhovered and the row looks
  lit in the middle and dead at both ends.
*/
.data-table-child-row[role='button']:hover {
  background: var(--data-table-child-row-hover);
}

.data-table-child-row[role='button']:hover .data-table-row-segment-left,
.data-table-child-row[role='button']:hover .data-table-row-segment-right {
  background: var(--data-table-child-row-hover);
}

.data-table-row.selected {
  background: var(--data-table-row-selected);
}

.data-table-cell {
  /* Anchors the expanded cell-text overlay below. */
  position: relative;
  flex: 1 1 0;
  padding: var(--data-table-cell-padding-y) var(--data-table-cell-padding-x);
  font-size: var(--data-table-cell-font-size);
  line-height: var(--data-table-cell-line-height);
  color: var(--agwa-text);
  border-right: 1px solid var(--data-table-divider);
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
}

/*
 * The overflow affordance (architect, 2026-09-05): a clipped cell single-lines its text with
 * an ellipsis, and resting the pointer on a genuinely cropped value reveals the whole of it
 * in place - see DataTableColumnDef.clip and handleCellTextHover for who clips and when the
 * overlay may appear. The expanded span leaves the flow, so it must repaint the cell's own
 * padding and a raised surface to stay readable over the neighbouring cells it covers.
 */
.cell-text {
  min-width: 0;
}

.cell-text--clip {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cell-text--expanded {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  /* Above neighbouring cells and row hover states; below the sticky header (z 10). */
  z-index: 4;
  width: max-content;
  overflow: visible;
  text-overflow: clip;
  background: var(--agwa-surface-raised);
  border: 1px solid var(--agwa-border);
  border-radius: var(--sl-border-radius-small);
  box-shadow: var(--agwa-shadow-lg);
  padding: var(--data-table-cell-padding-y) var(--data-table-cell-padding-x);
}

.data-table-cell.first {
  border-left: none;
}

/*
 * While a cell's clipped text is expanded, the cell itself is lifted: fixed cells are
 * stacking contexts (z-index 3), so the overlay's own z-index cannot escape its cell and
 * the NEXT fixed cell painted over it - the overlay sat full-width in layout yet visually
 * cut at the column edge. 6 clears the fixed cells' 3 and stays under the sticky header's
 * 10. Applied by handleCellTextHover, removed on collapse.
 */
/*
 * Scoped under .data-table deliberately: the legacy width helper at the END of this file
 * (".data-table .data-table-cell { overflow: hidden }") outranks a bare two-class selector
 * by order at equal specificity, and it is what clipped the overlay at the cell box in
 * every engine regardless of z-index - the paint probe showed the span absent from the
 * hit stack entirely, clipped rather than covered. Three classes beat it outright.
 */
.data-table .data-table-cell.cell-overflow-host {
  z-index: 6;
  overflow: visible;
}

.data-table-cell.last {
  border-right: none;
}

/* Compact embedded controls so action buttons and status tags do not
   inflate row height beyond the spreadsheet-tight baseline. */
.data-table-cell sl-button::part(base) {
  min-height: 0;
  height: var(--data-table-control-height);
  padding: 0 0.65rem;
  font-size: var(--data-table-cell-font-size);
  line-height: 1;
}

.data-table-cell sl-tag::part(base),
.data-table-cell sl-badge::part(base) {
  min-height: 0;
  height: auto;
  padding: 0.05rem 0.5rem;
  line-height: 1.3;
  font-size: calc(var(--data-table-cell-font-size) - 0.04rem);
}

.data-table-header .data-table-cell {
  background: var(--data-table-header-bg);
  font-size: var(--data-table-header-font-size);
  color: var(--data-table-header-color);
  border-right: 1px solid var(--agwa-on-accent-divider);
  font-weight: 600;
}

.data-table-row-extension {
  display: block;
  box-sizing: border-box;
  border-left: 3px solid var(--agwa-accent);
  border-bottom: 1px solid var(--data-table-divider);
  padding: 1rem 1.25rem;
  background: var(--data-table-row-extension-bg);
  color: var(--agwa-text);
}

/* Group header row - matches data row width, cell sticks left */
.data-table-group-header-row {
  border-bottom: 1px solid var(--data-table-divider);
  background: var(--agwa-accent-soft);
  position: relative;
}

/* Group header cell - sticky left, natural content width */
.data-table-group-header-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--agwa-accent-soft);
  border-left: 3px solid var(--agwa-accent);
  font-weight: 600;
  color: var(--agwa-accent-text);
  position: sticky;
  left: 0;
  z-index: 2;
}

/* Extend group header background to fill entire row during scroll */
.data-table-group-header-cell::after {
  content: '';
  position: absolute;
  top: 0;
  left: 100%;
  width: 100vw;
  height: 100%;
  background: var(--agwa-accent-soft);
  z-index: -1;
}

.data-table-group-header-label {
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  white-space: nowrap;
}

.data-table-group-header-count {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--agwa-text-muted);
  white-space: nowrap;
}

.data-table-load-more {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 1rem;
  border-top: 1px solid var(--data-table-divider);
  background: var(--agwa-surface-sunken);
}

.data-table-load-more .load-more-button {
  border-radius: 999px;
  border: none;
  background: linear-gradient(
    120deg,
    var(--agwa-accent),
    var(--agwa-accent-bright)
  );
  color: var(--agwa-on-accent);
  font-weight: 600;
  padding: 0.55rem 1.75rem;
  min-width: 180px;
  cursor: pointer;
  box-shadow: 0 8px 20px var(--agwa-accent-glow);
  transition:
    transform 160ms ease,
    box-shadow 160ms ease;
}

.data-table-load-more .load-more-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  box-shadow: none;
}

.data-table-load-more .load-more-button:not(:disabled):hover {
  transform: translateY(-1px);
}

.data-table-load-more .load-more-count {
  position: absolute;
  left: 1rem;
  font-size: 0.875rem;
  color: var(--agwa-text-muted);
  font-weight: 500;
}

/* Fullscreen toggle button */
.data-table-fullscreen-toggle {
  position: absolute;
  right: 1rem;
  font-size: 1.25rem;
  color: var(--agwa-accent-text);
}

.data-table-fullscreen-toggle:hover {
  color: var(--agwa-accent-strong);
}

/*
 * Fullscreen mode.
 *
 * LAYER: the same 400 as `agwa-fullscreen-block.styles.css`, and for the same reason stated there
 * - above the app masthead (`--layer-header: 150`, components/header-styles.css) and below every
 * Shoelace overlay (`--sl-z-index-drawer` 700, `--sl-z-index-dialog` 800, `--sl-z-index-toast`
 * 950), so a dialog or a toast raised from inside a full-screened table still lands on top of it.
 * THE TWO NOW AGREE: this used to be 9999, which defeated that reasoning and rendered any dialog
 * opened from a full-screened table behind the table. The architect's rule of 2026-09-19 -
 * "Dialogs should be seen" - is a rule for any full screen, not a patch for one, and
 * fullscreen-layer.node.spec.ts holds the two numbers to each other so a later edit cannot raise
 * one of them alone.
 */
:host(.data-table-fullscreen) {
  position: fixed !important;
  top: 0;
  left: 0;
  width: 100vw !important;
  height: 100vh !important;
  z-index: 400;
  border-radius: 0;
  overflow: hidden;
}

:host(.data-table-fullscreen) .data-table.data-table {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  border-radius: 0;
  overflow: hidden;
}

:host(.data-table-fullscreen) .data-table-scroll {
  max-height: none !important;
  flex: 1 1 0;
  min-height: 0;
}

:host(.data-table-fullscreen) .data-table-load-more {
  flex: 0 0 auto;
}

.data-table-cell--fixed {
  position: sticky;
  background: var(--data-table-bg);
  z-index: 2;
}

.data-table-content .data-table-row:hover .data-table-cell--fixed {
  background: var(--data-table-row-hover);
}

.data-table-content .data-table-row.selected .data-table-cell--fixed {
  background: var(--data-table-row-selected);
}

.data-table-header .data-table-cell--fixed {
  background: var(--data-table-header-bg);
  z-index: 5;
}

.data-table-cell--fixed-left {
  box-shadow: 8px 0 12px var(--agwa-shadow-edge);
}

.data-table-cell--fixed-right {
  box-shadow: -8px 0 12px var(--agwa-shadow-edge);
}

/* A right column that is not pinned (the table fits) casts no pinned edge's shadow. */
.data-table:not(.data-table--overflows-x) .data-table-cell--fixed-right {
  box-shadow: none;
}

.text-start,
.text-left {
  text-align: left;
  justify-content: flex-start;
}

.text-end,
.text-right {
  text-align: right;
  justify-content: flex-end;
}

.text-center {
  text-align: center;
  justify-content: center;
}

/* Legacy width helpers retained for compatibility */
.data-table .column,
.data-table .col,
.data-table .data-table-cell {
  overflow: hidden;
}

.data-table .col,
.data-table .data-table-cell {
  border-left: 1px solid var(--data-table-divider);
}

.data-table .col.first,
.data-table .data-table-cell.first {
  border-left: none;
}

.data-table .col.last,
.data-table .data-table-cell.last {
  border-right: none;
}

.data-table .tr {
  width: 100%;
}

/* Paginator row wrapper - matching load-more row */
.data-table-paginator-row {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 0.75rem 1rem;
  border-top: 1px solid var(--data-table-divider);
  background: var(--agwa-surface-sunken);
}

data-table-paginator {
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
}

/* NOTE: the paginator's internal rules (.pagination, .page-item, .page-link)
   used to live here, but <data-table-paginator> renders into its own shadow
   root, so descendant selectors from this stylesheet could never match them.
   They now live in data-table-paginator.styles.css, where they actually apply.
   Only the host-level rule above can style that element from here. */
